import {
	getDependencyCurrentFieldsWithoutOptions,
	getDependencyFieldsWithoutOptions,
} from '@utils/import/index';

import { ISystemFields, TCommonValue, TCommonValuesFields, TDependenciesImport } from '@interfaces/import/import';

import { EDefaultValueType } from '@enums/common';

export const generateDefaultFormFieldCustomValues = (systemFields: ISystemFields[]): TCommonValuesFields => {
	return systemFields.reduce((acc, field) => {
		acc = {
			...acc,
			[field.systemFieldName]: {
				...field,
				isUseDefaultValue: true,
				defaultValue: null,
			},
		};

		return acc;
	}, {});
};

export const preparedFieldsWithOptions = ({
	dependencies,
	defaultValue,
	formFields,
	updatedFormFields,
}: {
	dependencies: TDependenciesImport;
	defaultValue: TCommonValue | null;
	formFields: TCommonValuesFields;
	updatedFormFields: TCommonValuesFields;
}): void => {
	dependencies.forEach((dependencyFieldWidthOptions) => {
		const firstSystemFieldName = dependencyFieldWidthOptions.dependencyFields[0];
		const dependentSystemField = formFields[firstSystemFieldName];
		const firstOptionsBySelectedValue = dependencyFieldWidthOptions.dependencyValues
			.find(item => item.selectedValue === defaultValue)?.options || [];
		// set options
		Object.assign(updatedFormFields, {
			[firstSystemFieldName]: {
				...dependentSystemField,
				hasDefaultValue: !!firstOptionsBySelectedValue.length,
				isUseDefaultValue: true,
				defaultValue: null,
				systemFieldsDefaultData: {
					defaultValueType: EDefaultValueType.array,
					...dependentSystemField.systemFieldsDefaultData,
					options: firstOptionsBySelectedValue.length
						? firstOptionsBySelectedValue
						: undefined,
				},
			},
		});
	});
};

export const preparedRequiredFields = ({
	dependencies,
	defaultValue,
	updatedFormFields,
}: {
	dependencies: TDependenciesImport;
	defaultValue: TCommonValue | null;
	updatedFormFields: TCommonValuesFields;
}): void => {
	dependencies.forEach(dependency => {
		const isRequiredDependentFields = defaultValue !== null
			&& dependency.dependencyValues?.map(item => item.selectedValue).includes(defaultValue);

		//set is required to dependent field by custom value
		dependency.dependencyFields.forEach(dependentSystemFieldName => {
			if (Object.hasOwn(updatedFormFields, dependentSystemFieldName)) {
				const dependentSystemField = updatedFormFields[dependentSystemFieldName];

				Object.assign(updatedFormFields, {
					[dependentSystemFieldName]: {
						...dependentSystemField,
						isRequired: isRequiredDependentFields,
					},
				});
			}
		});
	});
};

export const generateFormFieldCustomValues = ({
		fieldName,
		defaultValue,
		formFields,
	}: {
		fieldName: string;
		defaultValue: TCommonValue | null;
		formFields: TCommonValuesFields;
	}): TCommonValuesFields | undefined => {
		if (Object.hasOwn(formFields, fieldName)) {
			const updatedFormFields = { ...formFields };
			const currentField = formFields[fieldName];

			if (currentField.hasDependency) {
				//set options to dependent custom value
				const shouldResetOptionsForDependentDefaultValue = !!currentField?.dependencies?.some(dependency =>
					dependency.dependencyValues.some(dependencyValue => dependencyValue?.options?.length),
				);

				if (shouldResetOptionsForDependentDefaultValue) {
					const dependencyFieldsWidthOptions = currentField?.dependencies
						?.filter(dependency =>
							dependency.dependencyValues.some(dependencyValue => dependencyValue?.options?.length),
						)
						?.map(dependency => ({
							...dependency,
							dependencyValues: dependency.dependencyValues
								.filter(dependencyValue => dependencyValue?.options?.length),
						}));

					const dependencyCurrentFieldsWidthOptions = dependencyFieldsWidthOptions
						?.filter(dependency => defaultValue && dependency.dependencyValues
							?.map(item => item.selectedValue).includes(defaultValue));

					if (dependencyCurrentFieldsWidthOptions?.length) {
						preparedFieldsWithOptions({
							dependencies: dependencyCurrentFieldsWidthOptions,
							defaultValue,
							formFields,
							updatedFormFields,
						});
						// reset by all dependent fields with options
					} else if (dependencyFieldsWidthOptions?.length) {
						preparedFieldsWithOptions({
							dependencies: dependencyFieldsWidthOptions,
							defaultValue,
							formFields,
							updatedFormFields,
						});
					}
				}

				// ONLY REQUIRED
				const dependencyFieldsWithoutOptions = getDependencyFieldsWithoutOptions({
					currentField,
					defaultValue,
				});

				const dependencyCurrentFieldsWithoutOptions = getDependencyCurrentFieldsWithoutOptions({
					currentField,
					defaultValue,
				});

				if (dependencyFieldsWithoutOptions?.length) {
					// reset by all dependent fields without options
					preparedRequiredFields({
						dependencies: dependencyFieldsWithoutOptions,
						defaultValue,
						updatedFormFields,
					});
				}
				// by selected/current field
				if (dependencyCurrentFieldsWithoutOptions?.length) {
					// set required fields
					preparedRequiredFields({
						dependencies: dependencyCurrentFieldsWithoutOptions,
						defaultValue,
						updatedFormFields,
					});
				}
			}

			return ({
				...updatedFormFields,
				[fieldName]: {
					...currentField,
					defaultValue: defaultValue,
				},
			});
		}
	}
;
